import { Button, Collapsible } from "ui";
import Drawer from "../../../components/Drawer";
import { useSandbox } from "../SandboxProvider";
import DateDisplay from "../../../components/DateDisplay";
import { useSandboxHistory } from "../hooks/useSandboxHistory";
import { CodeEditor } from "./CodeEditor";
import { Tab } from "@headlessui/react";
import { Fragment, useState } from "react";

type SandboxHistoryDrawerProps = {
  isOpen: boolean;
  onResponseChange: (response: Record<string, any>) => void;
  onClose: () => void;
};

export const SandboxHistoryDrawer = ({
  isOpen,
  onResponseChange,
  onClose,
}: SandboxHistoryDrawerProps) => {
  const { historyList } = useSandbox();
  const { updateSandboxState, updateUrlState } = useSandboxHistory();

  return (
    <Drawer
      isOpen={isOpen}
      title="Sandbox History"
      size="max-w-xl"
      onClose={onClose}
    >
      <Drawer.Content className="p-8">
        {historyList.length > 0 ? (
          <div className="flex flex-col space-y-6">
            {historyList.map((history, index) => (
              <HistoryItem
                key={history.time}
                versionNumber={historyList.length - index}
                history={history}
                onSelect={() => {
                  updateUrlState(history.sandboxState);
                  updateSandboxState(history.sandboxState);
                  setTimeout(() => onResponseChange(history.response), 500);
                  onClose();
                }}
              />
            ))}
          </div>
        ) : (
          <p className="font-medium text-foundations-subtle">No history yet!</p>
        )}
      </Drawer.Content>
      <Drawer.Footer>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
};

const HistoryItem = ({
  versionNumber,
  history,
  onSelect,
}: {
  versionNumber: number;
  history: any;
  onSelect: () => void;
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <div className="flex flex-1 flex-col space-y-3 rounded-lg border border-foundations-subtle px-6 py-4">
      <div className="flex items-center justify-between">
        <DateDisplay
          date={history.time}
          className="text-sm text-foundations-subtle"
        />

        <Button size="xs" colorScheme="inverse" onClick={onSelect}>
          View request
        </Button>
      </div>

      <div className="space-y-1">
        <p className="font-medium text-foundations-bolder">
          Version #{versionNumber} ({history.sandboxState.scenario})
        </p>
        <span className="text-xs font-medium text-foundations-subtle">
          Account: {history.sandboxState.account?.[1]}
        </span>
      </div>

      <Collapsible.Root onClick={(e) => e.stopPropagation()}>
        <Collapsible.Trigger>
          <span className="text-sm font-medium text-foundations-bold">
            Request/Response Data
          </span>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            <Tab.List className="flex space-x-4">
              <Tab
                className="flex items-center space-x-1.5 border-b-2 border-b-transparent px-2 pb-2.5 pt-1 text-sm font-medium text-foundations-subtle focus:outline-none aria-selected:border-b-blue-500 aria-selected:text-foundations-boldest"
                onClick={(e) => e.stopPropagation()}
              >
                <span>Request</span>
              </Tab>
              <Tab
                className="flex items-center space-x-1.5 border-b-2 border-b-transparent px-2 pb-2.5 pt-1 text-sm font-medium text-foundations-subtle focus:outline-none aria-selected:border-b-blue-500 aria-selected:text-foundations-boldest"
                onClick={(e) => e.stopPropagation()}
              >
                <span>Response</span>
              </Tab>
            </Tab.List>

            <Tab.Panels as={Fragment}>
              <Tab.Panel className="mt-4 flex flex-1">
                <CodeEditor
                  className="w-0 flex-1"
                  readOnly
                  value={JSON.stringify(history.request || {}, null, 2)}
                />
              </Tab.Panel>

              <Tab.Panel className="mt-4 flex flex-1">
                <CodeEditor
                  className="w-0 flex-1"
                  readOnly
                  value={JSON.stringify(history.response || {}, null, 2)}
                />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};
